import React, { useState, useEffect, useRef } from "react";
import { toast, Toaster } from "react-hot-toast";
import "./Homepage.css";

const API = "https://admin.riymay.com/api/v1/admin";

const Homepage = () => {
  const [tab, setTab] = useState("latestdrop");

  // ── Latest Drop state ──
  const [shelves, setShelves] = useState([]);
  const [shelfLoading, setShelfLoading] = useState(true);
  const [newShelfName, setNewShelfName] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [activeShelfId, setActiveShelfId] = useState(null);
  const [editingShelfName, setEditingShelfName] = useState({});
  const searchTimeout = useRef(null);

  // ── Collections state ──
  const [collections, setCollections] = useState([]);
  const [colLoading, setColLoading] = useState(true);
  const [newColTitle, setNewColTitle] = useState("");
  const [newColFile, setNewColFile] = useState(null);
  const [editingCol, setEditingCol] = useState(null);
  const [categories, setCategories] = useState([]);

  const token = localStorage.getItem("admintoken");
  const headers = { Authorization: `Bearer ${token}` };

  // ── Fetch shelves ──
  useEffect(() => {
    fetchShelves();
  }, []);

  // ── Fetch collections ──
  useEffect(() => {
    fetchCollections();
  }, []);

  // ── Fetch categories for collection link picker ──
  useEffect(() => {
    fetch(`${API}/get/all/main/product-categories`)
      .then(r => r.json())
      .then(d => setCategories(d.mainProductCategories || []))
      .catch(() => {});
  }, []);

  async function fetchShelves() {
    setShelfLoading(true);
    try {
      const res = await fetch(`${API}/homepage/shelves`);
      const data = await res.json();
      setShelves(data.shelves || []);
    } catch { toast.error("Failed to load shelves"); }
    setShelfLoading(false);
  }

  async function fetchCollections() {
    setColLoading(true);
    try {
      const res = await fetch(`${API}/homepage/collections`);
      const data = await res.json();
      setCollections(data.banners || []);
    } catch { toast.error("Failed to load collections"); }
    setColLoading(false);
  }

  // ── Create shelf ──
  async function handleCreateShelf() {
    if (!newShelfName.trim()) return toast.error("Enter a shelf name");
    try {
      const res = await fetch(`${API}/homepage/shelf`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: newShelfName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || res.status);
      setNewShelfName("");
      toast.success("Shelf created");
      fetchShelves();
    } catch (err) { toast.error("Shelf error: " + err.message); }
  }

  // ── Rename shelf ──
  async function handleRenameShelf(shelfId, name) {
    try {
      const res = await fetch(`${API}/homepage/shelf`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ shelfId, name }),
      });
      if (!res.ok) throw new Error();
      toast.success("Renamed");
      fetchShelves();
    } catch { toast.error("Failed to rename"); }
  }

  // ── Delete shelf ──
  async function handleDeleteShelf(shelfId) {
    if (!window.confirm("Delete this shelf?")) return;
    try {
      const res = await fetch(`${API}/homepage/shelf`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ shelfId }),
      });
      if (!res.ok) throw new Error();
      toast.success("Shelf deleted");
      fetchShelves();
    } catch { toast.error("Failed to delete shelf"); }
  }

  // ── Load all products when shelf panel opens ──
  async function loadAllProducts() {
    try {
      const res = await fetch(`${API}/search-clothing-product?searchQuery=&page=1&limit=50`, { headers });
      const data = await res.json();
      setSearchResults(data.products || []);
    } catch { setSearchResults([]); }
  }

  // ── Search products ──
  function handleProductSearch(query) {
    setProductSearch(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!query.trim()) { loadAllProducts(); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API}/search-clothing-product?searchQuery=${encodeURIComponent(query)}`,
          { headers }
        );
        const data = await res.json();
        setSearchResults(data.products || []);
      } catch { setSearchResults([]); }
    }, 350);
  }

  // ── Add product to shelf ──
  async function handleAddProductToShelf(shelfId, shelf, product) {
    const alreadyIn = shelf.products.some(p => p._id === product._id);
    if (alreadyIn) return toast.error("Already on this shelf");
    const updatedIds = [...shelf.products.map(p => p._id), product._id];
    try {
      const res = await fetch(`${API}/homepage/shelf`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ shelfId, products: updatedIds }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Added "${product.name}"`);
      fetchShelves();
    } catch { toast.error("Failed to add product"); }
  }

  // ── Remove product from shelf ──
  async function handleRemoveFromShelf(shelfId, shelf, productId) {
    const updatedIds = shelf.products.map(p => p._id).filter(id => id !== productId);
    try {
      const res = await fetch(`${API}/homepage/shelf`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ shelfId, products: updatedIds }),
      });
      if (!res.ok) throw new Error();
      toast.success("Removed");
      fetchShelves();
    } catch { toast.error("Failed to remove"); }
  }

  // ── Create collection ──
  async function handleCreateCollection() {
    if (!newColTitle.trim()) return toast.error("Enter a title");
    const form = new FormData();
    form.append("title", newColTitle.trim());
    form.append("link", "/newproduct");
    if (newColFile) form.append("file", newColFile);
    try {
      const res = await fetch(`${API}/homepage/collection`, {
        method: "POST",
        headers,
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || res.status);
      setNewColTitle(""); setNewColFile(null);
      toast.success("Collection added");
      fetchCollections();
    } catch (err) { toast.error("Error: " + err.message); }
  }

  // ── Update collection ──
  async function handleUpdateCollection() {
    if (!editingCol) return;
    const form = new FormData();
    form.append("bannerId", editingCol._id);
    form.append("title", editingCol.title);
    form.append("link", editingCol.link);
    if (editingCol.newFile) form.append("file", editingCol.newFile);
    try {
      const res = await fetch(`${API}/homepage/collection`, {
        method: "PUT",
        headers,
        body: form,
      });
      if (!res.ok) throw new Error();
      setEditingCol(null);
      toast.success("Updated");
      fetchCollections();
    } catch { toast.error("Failed to update"); }
  }

  // ── Delete collection ──
  async function handleDeleteCollection(bannerId) {
    if (!window.confirm("Delete this collection?")) return;
    try {
      const res = await fetch(`${API}/homepage/collection`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ bannerId }),
      });
      if (!res.ok) throw new Error();
      toast.success("Deleted");
      fetchCollections();
    } catch { toast.error("Failed to delete"); }
  }

  return (
    <div className="hp-container">
      <Toaster />
      <h2 className="hp-title">Homepage Manager</h2>

      <div className="hp-tabs">
        <button className={`hp-tab ${tab === "latestdrop" ? "active" : ""}`} onClick={() => setTab("latestdrop")}>Latest Drop</button>
        <button className={`hp-tab ${tab === "collections" ? "active" : ""}`} onClick={() => setTab("collections")}>Collections</button>
      </div>

      {/* ── LATEST DROP TAB ── */}
      {tab === "latestdrop" && (
        <div className="hp-section">
          <div className="hp-create-row">
            <input
              className="hp-input"
              placeholder="New shelf name (e.g. Oversized Tees)"
              value={newShelfName}
              onChange={e => setNewShelfName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreateShelf()}
            />
            <button className="hp-btn-primary" onClick={handleCreateShelf}>+ Create Shelf</button>
          </div>

          {shelfLoading ? <p className="hp-loading">Loading shelves…</p> : (
            <div className="hp-shelves">
              {shelves.length === 0 && <p className="hp-empty">No shelves yet. Create one above.</p>}
              {shelves.map(shelf => (
                <div key={shelf._id} className="hp-shelf">
                  <div className="hp-shelf-header">
                    {editingShelfName[shelf._id] !== undefined ? (
                      <input
                        className="hp-input hp-shelf-name-input"
                        value={editingShelfName[shelf._id]}
                        onChange={e => setEditingShelfName(prev => ({ ...prev, [shelf._id]: e.target.value }))}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            handleRenameShelf(shelf._id, editingShelfName[shelf._id]);
                            setEditingShelfName(prev => { const n = { ...prev }; delete n[shelf._id]; return n; });
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <h3 className="hp-shelf-name">{shelf.name}</h3>
                    )}
                    <div className="hp-shelf-actions">
                      <button className="hp-btn-sm" onClick={() =>
                        setEditingShelfName(prev =>
                          prev[shelf._id] !== undefined
                            ? (() => { const n = { ...prev }; delete n[shelf._id]; return n; })()
                            : { ...prev, [shelf._id]: shelf.name }
                        )
                      }>
                        {editingShelfName[shelf._id] !== undefined ? "Cancel" : "Rename"}
                      </button>
                      {editingShelfName[shelf._id] !== undefined && (
                        <button className="hp-btn-primary hp-btn-sm" onClick={() => {
                          handleRenameShelf(shelf._id, editingShelfName[shelf._id]);
                          setEditingShelfName(prev => { const n = { ...prev }; delete n[shelf._id]; return n; });
                        }}>Save</button>
                      )}
                      <button className="hp-btn-danger hp-btn-sm" onClick={() => handleDeleteShelf(shelf._id)}>Delete</button>
                      <button className="hp-btn-sm" onClick={() => {
                        const opening = activeShelfId !== shelf._id;
                        setActiveShelfId(opening ? shelf._id : null);
                        setProductSearch("");
                        if (opening) loadAllProducts();
                      }}>
                        {activeShelfId === shelf._id ? "Close" : "+ Add Products"}
                      </button>
                    </div>
                  </div>

                  {/* Products on shelf */}
                  <div className="hp-shelf-products">
                    {shelf.products.length === 0 && <span className="hp-empty-small">No products on this shelf yet.</span>}
                    {shelf.products.map(p => (
                      <div key={p._id} className="hp-shelf-product">
                        {p.images && p.images[0] && (
                          <img src={`https://riymay.com/assets/${p.images[0].url}`} alt={p.name} className="hp-product-thumb" />
                        )}
                        <div className="hp-product-info">
                          <span className="hp-product-name">{p.name}</span>
                          <span className="hp-product-price">₹{p.price}</span>
                        </div>
                        <button className="hp-btn-danger hp-btn-sm" onClick={() => handleRemoveFromShelf(shelf._id, shelf, p._id)}>Remove</button>
                      </div>
                    ))}
                  </div>

                  {/* Add product search */}
                  {activeShelfId === shelf._id && (
                    <div className="hp-product-search">
                      <input
                        className="hp-input"
                        placeholder="Search products by name…"
                        value={productSearch}
                        onChange={e => handleProductSearch(e.target.value)}
                      />
                      {searchResults.length > 0 && (
                        <div className="hp-search-results">
                          {searchResults.map(p => (
                            <div key={p._id} className="hp-search-result" onClick={() => handleAddProductToShelf(shelf._id, shelf, p)}>
                              <span>{p.name}</span>
                              <span className="hp-result-price">₹{p.price}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── COLLECTIONS TAB ── */}
      {tab === "collections" && (
        <div className="hp-section">
          <div className="hp-col-form">
            <h3 className="hp-sub-title">Add New Collection</h3>
            <input className="hp-input" placeholder="Collection name (e.g. Essentials)" value={newColTitle} onChange={e => setNewColTitle(e.target.value)} />
            <div className="hp-file-row">
              <label className="hp-file-label">
                Banner Image
                <input type="file" accept="image/*" onChange={e => setNewColFile(e.target.files[0])} />
              </label>
              {newColFile && <span className="hp-file-name">{newColFile.name}</span>}
            </div>
            <button className="hp-btn-primary" onClick={handleCreateCollection}>+ Add Collection</button>
          </div>

          {colLoading ? <p className="hp-loading">Loading…</p> : (
            <div className="hp-collections-list">
              {collections.length === 0 && <p className="hp-empty">No collections yet.</p>}
              {collections.map(col => (
                <div key={col._id} className="hp-col-item">
                  {col.imageUrl && (
                    <img src={`https://riymay.com/assets/${col.imageUrl}`} alt={col.title} className="hp-col-thumb" />
                  )}
                  {editingCol && editingCol._id === col._id ? (
                    <div className="hp-col-edit">
                      <input className="hp-input" value={editingCol.title} onChange={e => setEditingCol(p => ({ ...p, title: e.target.value }))} placeholder="Collection name" />
                      <div className="hp-file-row">
                        <label className="hp-file-label">
                          Replace Image
                          <input type="file" accept="image/*" onChange={e => setEditingCol(p => ({ ...p, newFile: e.target.files[0] }))} />
                        </label>
                      </div>
                      <div className="hp-col-edit-btns">
                        <button className="hp-btn-primary hp-btn-sm" onClick={handleUpdateCollection}>Save</button>
                        <button className="hp-btn-sm" onClick={() => setEditingCol(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="hp-col-info">
                      <span className="hp-col-title">{col.title}</span>
                      <span className="hp-col-link">{col.link}</span>
                    </div>
                  )}
                  <div className="hp-col-btns">
                    <button className="hp-btn-sm" onClick={() => setEditingCol({ ...col, newFile: null })}>Edit</button>
                    <button className="hp-btn-danger hp-btn-sm" onClick={() => handleDeleteCollection(col._id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Homepage;
